/**
 * AIDAS Component Showcase Page
 * Developer documentation for the centralized component library.
 * Route: /components (publicly accessible — no auth required)
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, useTheme, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { addDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import dayjs, { Dayjs } from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import {
  PlusCircle, Trash2, Download, RefreshCcw, Search, Database,
  ChevronRight, ChevronDown, AlertTriangle, Info, CheckCircle2, XCircle, Eye, Copy, Code2, Layers,
  Shield, MousePointerClick, TextCursorInput, ListFilter, SlidersHorizontal, Calendar,
  ToggleLeft, Tag, Circle, LayoutGrid, BarChart3, ChevronsUpDown, PanelTop,
  Table2, User, Clock, Navigation, TrendingUp, MessageSquare, Upload, PenLine,
  Columns3, Loader2, Bell, PanelRight, HelpCircle, MessageCircle, Megaphone,
  ArrowLeftRight, Footprints, MoreHorizontal, Inbox, Zap, RotateCcw, FileText,
  Component, BookOpen, Ban, Check, Minus
} from 'lucide-react';


// Core imports — all from barrel
import {
  Button, Input, Textarea, Checkbox, RadioGroup, Switch,
  Select, Slider, DatePicker,
  Badge, Card, CardHeader, CardContent, CardFooter, EmptyState,
  Accordion, Tabs, Tooltip, Avatar, AvatarGroup,
  Timeline, TimelineItem, TimelineHeader, TimelineIndicator,
  TimelineTitle, TimelineContent, SimpleTimeline,
  StatCard, Breadcrumbs, DataTable, MuiDataGrid, MuiDateTimePicker, Chip, StatusDot, Progress,
  SearchInput, FileUpload, SimpleFileUpload, getReadableFileSize, InlineEdit,
  Skeleton, SkeletonCard, Spinner, Alert,
  Dialog, DialogFooter, Drawer, DrawerFooter, ConfirmDialog, notify, Popover,
  Pagination, Stepper,
  ActionButton, ViewToggle, DropdownMenu, CopyButton, RefreshButton, DeleteButton,
  PageHeader, Stack, Grid, FilterPanel, Can
} from '@/components/core';
import { PERMISSIONS } from '@/constants/permissions';

// ── Section wrapper for grouping ─────────────────────────────────────────
const Section: React.FC<{ title: string; description: string; children: React.ReactNode; id?: string }> = ({ title, description, children, id }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="mb-10"
  >
    <div className="mb-4">
      <Typography variant="h5" fontWeight="700" color="text.primary" className="flex items-center gap-2">
        <div className="h-6 w-1 rounded-full bg-gradient-to-b from-[var(--primary-color)] to-blue-500" />
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    </div>
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {children}
    </Paper>
  </motion.div>
);

// ── Code display helper ─────────────────────────────────────────────────
const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="mt-3 p-3 rounded-lg bg-slate-900 dark:bg-slate-950 text-green-400 text-xs font-mono overflow-x-auto border border-slate-800">
    <code>{children}</code>
  </pre>
);

const ImportBlock: React.FC<{ code: string }> = ({ code }) => (
  <div className="mt-2 flex items-center gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-200 dark:border-slate-700">
    <Code2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
    <code className="text-xs text-slate-600 dark:text-slate-300 font-mono flex-1 overflow-x-auto">{code}</code>
    <button onClick={() => { navigator.clipboard.writeText(code); notify.success('Copied!'); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0">
      <Copy className="h-3.5 w-3.5" />
    </button>
  </div>
);

// ── Sidebar TOC ─────────────────────────────────────────────────────────
const tocItems: { id: string; label: string; icon: React.ReactNode }[] = [
  { id: 'rbac', label: 'RBAC Gates', icon: <Shield className="h-3.5 w-3.5" /> },
  { id: 'buttons', label: 'Buttons', icon: <MousePointerClick className="h-3.5 w-3.5" /> },
  { id: 'inputs', label: 'Inputs & Textarea', icon: <TextCursorInput className="h-3.5 w-3.5" /> },
  { id: 'select', label: 'Select', icon: <ListFilter className="h-3.5 w-3.5" /> },
  { id: 'slider', label: 'Slider', icon: <SlidersHorizontal className="h-3.5 w-3.5" /> },
  { id: 'datepicker', label: 'DatePicker', icon: <Calendar className="h-3.5 w-3.5" /> },
  { id: 'checkboxes', label: 'Checkbox, Radio, Switch', icon: <ToggleLeft className="h-3.5 w-3.5" /> },
  { id: 'badges', label: 'Badges & Tags', icon: <Tag className="h-3.5 w-3.5" /> },
  { id: 'chips', label: 'Chip & StatusDot', icon: <Circle className="h-3.5 w-3.5" /> },
  { id: 'cards', label: 'Cards', icon: <LayoutGrid className="h-3.5 w-3.5" /> },
  { id: 'statcard', label: 'Stat Cards', icon: <BarChart3 className="h-3.5 w-3.5" /> },
  { id: 'accordion', label: 'Accordion', icon: <ChevronsUpDown className="h-3.5 w-3.5" /> },
  { id: 'tabs', label: 'Tabs', icon: <PanelTop className="h-3.5 w-3.5" /> },
  { id: 'datatable', label: 'Data Table', icon: <Table2 className="h-3.5 w-3.5" /> },
  { id: 'muidatagrid', label: 'MUI DataGrid', icon: <Table2 className="h-3.5 w-3.5" /> },
  { id: 'muidatetimepicker', label: 'MUI DateTimePicker', icon: <Calendar className="h-3.5 w-3.5" /> },
  { id: 'avatar', label: 'Avatar', icon: <User className="h-3.5 w-3.5" /> },
  { id: 'timeline', label: 'Timeline', icon: <Clock className="h-3.5 w-3.5" /> },
  { id: 'breadcrumbs', label: 'Breadcrumbs', icon: <Navigation className="h-3.5 w-3.5" /> },
  { id: 'progress', label: 'Progress', icon: <TrendingUp className="h-3.5 w-3.5" /> },
  { id: 'tooltip', label: 'Tooltip', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { id: 'search', label: 'Search Input', icon: <Search className="h-3.5 w-3.5" /> },
  { id: 'fileupload', label: 'File Upload', icon: <Upload className="h-3.5 w-3.5" /> },
  { id: 'inlineedit', label: 'Inline Edit', icon: <PenLine className="h-3.5 w-3.5" /> },
  { id: 'view-toggle', label: 'View Toggle', icon: <Columns3 className="h-3.5 w-3.5" /> },
  { id: 'feedback', label: 'Skeleton & Spinners', icon: <Loader2 className="h-3.5 w-3.5" /> },
  { id: 'alerts', label: 'Alerts', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
  { id: 'dialog', label: 'Dialog & Drawer', icon: <PanelRight className="h-3.5 w-3.5" /> },
  { id: 'confirm', label: 'Confirm Dialog', icon: <HelpCircle className="h-3.5 w-3.5" /> },
  { id: 'popover', label: 'Popover', icon: <MessageCircle className="h-3.5 w-3.5" /> },
  { id: 'toast', label: 'Toast Notifications', icon: <Megaphone className="h-3.5 w-3.5" /> },
  { id: 'pagination', label: 'Pagination', icon: <ArrowLeftRight className="h-3.5 w-3.5" /> },
  { id: 'stepper', label: 'Stepper', icon: <Footprints className="h-3.5 w-3.5" /> },
  { id: 'dropdown', label: 'Dropdown Menu', icon: <MoreHorizontal className="h-3.5 w-3.5" /> },
  { id: 'empty', label: 'Empty State', icon: <Inbox className="h-3.5 w-3.5" /> },
  { id: 'actions', label: 'Action Buttons', icon: <Zap className="h-3.5 w-3.5" /> },
  { id: 'copyrefresh', label: 'Copy, Refresh, Delete', icon: <RotateCcw className="h-3.5 w-3.5" /> },
  { id: 'pageheader', label: 'Page Header', icon: <FileText className="h-3.5 w-3.5" /> },
  { id: 'layout', label: 'Stack, Grid & Filters', icon: <Component className="h-3.5 w-3.5" /> },
  { id: 'rules', label: 'Usage Rules', icon: <BookOpen className="h-3.5 w-3.5" /> },
];

export default function ComponentShowcasePage() {
  const theme = useTheme();
  const [demoSearch, setDemoSearch] = useState('');
  const [demoView, setDemoView] = useState<'grid' | 'list'>('grid');
  const [demoCheckbox, setDemoCheckbox] = useState(false);
  const [demoRadio, setDemoRadio] = useState('option1');
  const [demoSwitch, setDemoSwitch] = useState(true);
  const [activeTocId, setActiveTocId] = useState('rbac');
  const [demoSelect, setDemoSelect] = useState('');
  const [demoSlider, setDemoSlider] = useState(50);
  const [demoDate, setDemoDate] = useState<Date | undefined>(undefined);
  const [demoRange, setDemoRange] = useState<DateRange | undefined>(undefined);
  const [demoDob, setDemoDob] = useState<Date | undefined>(undefined);
  const [demoDateTime, setDemoDateTime] = useState<Date | undefined>(undefined);
  const [demoMuiDateTime, setDemoMuiDateTime] = useState<Dayjs | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(false);
  const [demoTab, setDemoTab] = useState('tab1');
  const [demoPage, setDemoPage] = useState(1);
  const [demoStep, setDemoStep] = useState(1);
  const [inlineVal, setInlineVal] = useState('Click to edit me');

  // ── Activate design-system tokens for this public (un-authed) page ──────
  // The `tenant-portal` CSS class + CSS variables are normally applied by
  // ThemeContext only when a tenant user is logged in.  Since this route
  // renders outside AuthGate, we manually activate them here so every
  // component renders with the correct border-radius and primary color.
  useEffect(() => {
    const root = document.documentElement;
    const wasPresent = root.classList.contains('tenant-portal');
    root.classList.add('tenant-portal');

    // Ensure default design tokens are present (ThemeContext may not have run)
    const defaults: Record<string, string> = {
      '--primary-color': '#3b82f6',
      '--primary-rgb': '59 130 246',
      '--primary-foreground': '#ffffff',
      '--secondary-color': '#64748b',
      '--border-radius': '8px',
      '--font-family': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    };
    const prev: Record<string, string> = {};
    for (const [key, val] of Object.entries(defaults)) {
      prev[key] = root.style.getPropertyValue(key);
      if (!root.style.getPropertyValue(key)) {
        root.style.setProperty(key, val);
      }
    }

    return () => {
      if (!wasPresent) root.classList.remove('tenant-portal');
      for (const [key, val] of Object.entries(prev)) {
        if (val) root.style.setProperty(key, val);
        else root.style.removeProperty(key);
      }
    };
  }, []);

  const scrollTo = (id: string) => {
    setActiveTocId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', minHeight: 0, overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* ── Left TOC Sidebar ── */}
      <Box sx={{
        width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider',
        overflowY: 'auto', py: 2, px: 1.5,
        display: { xs: 'none', md: 'block' },
      }}>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ px: 1, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
          Components
        </Typography>
        {tocItems.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollTo(item.id)}
            className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors mb-0.5 ${
              activeTocId === item.id
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
          >
            <span className="shrink-0 opacity-60">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </Box>

      {/* ── Main Content ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-gradient-to-br from-[var(--primary-color)] to-blue-600 text-white shadow-lg">
              <Layers className="h-6 w-6" />
            </div>
            <div>
              <Typography variant="h4" fontWeight="800" color="text.primary">
                Component Library
              </Typography>
              <Typography variant="body2" color="text.secondary">
                AIDAS Design System — Enterprise-grade reusable components
              </Typography>
            </div>
          </div>
          <ImportBlock code={`import { Button, Badge, Can, ActionButton, Card } from '@/components/core';`} />
        </motion.div>

        {/* ── RBAC GATES ── */}
        <Section id="rbac" title="RBAC Permission Gates" description="Declarative wrappers that show/hide UI based on user permissions. Never use manual if-checks.">
          <div className="space-y-4">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>{'<Can>'} — Declarative wrapper</Typography>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                <Can permission={PERMISSIONS.DATASOURCES_CREATE}>
                  <Badge variant="success">Visible — you have datasources:create</Badge>
                </Can>
                <Can permission="nonexistent:permission" fallback={<Badge variant="danger">Permission denied (fallback UI)</Badge>}>
                  <Badge variant="success">Hidden content</Badge>
                </Can>
              </div>
              <CodeBlock>{`<Can permission={PERMISSIONS.DATASOURCES_CREATE}>
  <Button>Create Data Source</Button>
</Can>

<Can permission="admin:only" fallback={<span>No access</span>}>
  <SecretPanel />
</Can>`}</CodeBlock>
            </div>
            <Divider sx={{ my: 2 }} />
            <div>
              <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>usePermissionGate() — Hook</Typography>
              <CodeBlock>{`const { allowed: canDelete } = usePermissionGate(PERMISSIONS.USERS_DELETE);
if (canDelete) showDeleteButton();`}</CodeBlock>
            </div>
            <Divider sx={{ my: 2 }} />
            <div>
              <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>withPermission() — HOC</Typography>
              <CodeBlock>{`const ProtectedPage = withPermission(AdminPage, PERMISSIONS.USERS_MANAGE);
// Renders "Access Denied" if user lacks permission`}</CodeBlock>
            </div>
          </div>
        </Section>

        {/* ── BUTTONS ── */}
        <Section id="buttons" title="Buttons" description="Unified button with 6 variants, 5 sizes, loading state, icons, and optional RBAC gating.">
          <div className="space-y-4">
            <Typography variant="subtitle2" fontWeight="bold">Variants</Typography>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="link">Link</Button>
            </div>

            <Typography variant="subtitle2" fontWeight="bold">Sizes</Typography>
            <div className="flex flex-wrap items-center gap-3">
              <Button size="xs">Extra Small</Button>
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
            </div>

            <Typography variant="subtitle2" fontWeight="bold">With Icons</Typography>
            <div className="flex flex-wrap gap-3">
              <Button leftIcon={<PlusCircle className="h-4 w-4" />}>Create</Button>
              <Button variant="danger" leftIcon={<Trash2 className="h-4 w-4" />}>Delete</Button>
              <Button variant="outline" leftIcon={<Download className="h-4 w-4" />}>Export</Button>
              <Button variant="ghost" rightIcon={<ChevronRight className="h-4 w-4" />}>Next</Button>
            </div>

            <Typography variant="subtitle2" fontWeight="bold">States</Typography>
            <div className="flex flex-wrap gap-3">
              <Button loading>Loading...</Button>
              <Button disabled>Disabled</Button>
              <Button fullWidth variant="secondary">Full Width</Button>
            </div>

            <Typography variant="subtitle2" fontWeight="bold">RBAC-Gated (hidden if no permission)</Typography>
            <div className="flex flex-wrap gap-3">
              <Button permission={PERMISSIONS.DATASOURCES_CREATE} leftIcon={<PlusCircle className="h-4 w-4" />}>
                Gated: Create Source
              </Button>
              <Button permission="fake:permission" leftIcon={<PlusCircle className="h-4 w-4" />}>
                Hidden: No Permission
              </Button>
            </div>

            <CodeBlock>{`<Button variant="primary" size="md" leftIcon={<PlusCircle />} loading={isSubmitting}>
  Save Changes
</Button>

// RBAC: auto-hides if user lacks permission
<Button permission={PERMISSIONS.DATASOURCES_CREATE}>Create</Button>`}</CodeBlock>
          </div>
        </Section>

        {/* ── INPUTS ── */}
        <Section id="inputs" title="Inputs & Textarea" description="Form inputs with MUI TextField integration, icon support, error states, and read-only mode.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Default Input" placeholder="Enter value..." />
            <Input label="With Left Icon" leftIcon={<Search className="h-4 w-4" />} placeholder="Search..." />
            <Input label="Error State" error helperText="This field is required" />
            <Input label="Read Only" value="Cannot edit this" readOnly />
            <Input label="Disabled" disabled placeholder="Disabled input" />
            <Input label="Password" type="password" placeholder="••••••" />
          </div>
          <div className="mt-4">
            <Textarea label="Textarea" placeholder="Enter long description..." rows={3} />
          </div>
          <CodeBlock>{`<Input label="Email" placeholder="user@example.com" leftIcon={<Mail />} />
<Input label="Name" error helperText="Required" />
<Textarea label="Notes" rows={4} />`}</CodeBlock>
        </Section>

        {/* ── SELECT ── */}
        <Section id="select" title="Select" description="Dropdown select with search, multi-select, groups, and custom rendering.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Basic Select" value={demoSelect} onChange={(v) => setDemoSelect(typeof v === 'string' ? v : '')} options={[
              { value: 'pg', label: 'PostgreSQL' },
              { value: 'mysql', label: 'MySQL' },
              { value: 'mongo', label: 'MongoDB' },
            ]} placeholder="Choose database..." />
            <Select label="Disabled" disabled options={[{ value: 'x', label: 'Disabled' }]} />
          </div>
          <CodeBlock>{`<Select label="DB" value={val} onChange={setVal} options={[...]} />`}</CodeBlock>
        </Section>

        {/* ── SLIDER ── */}
        <Section id="slider" title="Slider" description="Range input for numeric values with labels and step support.">
          <div className="max-w-sm">
            <Slider label={`Volume: ${demoSlider}%`} value={demoSlider} onChange={(v) => setDemoSlider(typeof v === 'number' ? v : v[0])} min={0} max={100} step={5} />
          </div>
          <CodeBlock>{`<Slider label="Volume" value={val} onChange={setVal} min={0} max={100} />`}</CodeBlock>
        </Section>

        {/* ── DATEPICKER ── */}
        <Section id="datepicker" title="DatePicker" description="Composable date picker built from Popover + Calendar (react-day-picker). Supports single, range, DOB, time, and presets.">
          <div className="space-y-6">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Basic</Typography>
              <div className="max-w-xs">
                <DatePicker label="Start Date" value={demoDate} onChange={setDemoDate} />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Range Picker</Typography>
              <div className="max-w-md">
                <DatePicker mode="range" label="Date Range" range={demoRange} onRangeChange={setDemoRange} />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Date of Birth (dropdown caption)</Typography>
              <div className="max-w-xs">
                <DatePicker label="Date of Birth" value={demoDob} onChange={setDemoDob} captionLayout="dropdown" fromYear={1920} toYear={2010} />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>With Time</Typography>
              <div className="max-w-xs">
                <DatePicker label="Meeting Time" value={demoDateTime} onChange={setDemoDateTime} includeTime />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>With Presets</Typography>
              <div className="max-w-xs">
                <DatePicker label="Due Date" value={demoDate} onChange={setDemoDate} presets={[
                  { label: 'Today', value: new Date() },
                  { label: 'Tomorrow', value: addDays(new Date(), 1) },
                  { label: 'In 3 days', value: addDays(new Date(), 3) },
                  { label: 'In a week', value: addDays(new Date(), 7) },
                  { label: 'In 2 weeks', value: addDays(new Date(), 14) },
                ]} />
              </div>
            </div>
          </div>
          <CodeBlock>{`// Basic
<DatePicker label="Start Date" value={date} onChange={setDate} />

// Range
<DatePicker mode="range" range={range} onRangeChange={setRange} />

// Date of birth with dropdowns
<DatePicker captionLayout="dropdown" fromYear={1920} toYear={2010} />

// With time input
<DatePicker includeTime value={dt} onChange={setDt} />

// With presets
<DatePicker presets={[{ label: 'Today', value: new Date() }]} />`}</CodeBlock>
        </Section>

        {/* ── CHECKBOX, RADIO, SWITCH ── */}
        <Section id="checkboxes" title="Checkbox, Radio & Switch" description="Selection controls built on Radix primitives for full keyboard accessibility.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Checkbox</Typography>
              <div className="space-y-2">
                <Checkbox id="c1" label="Enable feature" checked={demoCheckbox} onCheckedChange={setDemoCheckbox} />
                <Checkbox id="c2" label="Disabled option" disabled />
                <Checkbox id="c3" label="Pre-checked" checked />
              </div>
            </div>
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Radio Group</Typography>
              <RadioGroup
                value={demoRadio}
                onValueChange={setDemoRadio}
                options={[
                  { value: 'option1', label: 'Option One' },
                  { value: 'option2', label: 'Option Two' },
                  { value: 'option3', label: 'Disabled', disabled: true },
                ]}
              />
            </div>
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Switch (Toggle)</Typography>
              <div className="space-y-2">
                <Switch id="s1" label="Dark mode" checked={demoSwitch} onCheckedChange={setDemoSwitch} />
                <Switch id="s2" label="Small switch" size="sm" checked />
                <Switch id="s3" label="Disabled" disabled />
              </div>
            </div>
          </div>
          <CodeBlock>{`<Checkbox label="Enable feature" checked={val} onCheckedChange={setVal} />
<RadioGroup value={radio} onValueChange={setRadio} options={[...]} />
<Switch label="Dark mode" checked={dark} onCheckedChange={setDark} />`}</CodeBlock>
        </Section>

        {/* ── BADGES ── */}
        <Section id="badges" title="Badges & Tags" description="Status indicators with 6 variants, dot indicators, and icon support.">
          <div className="flex flex-wrap gap-3">
            <Badge>Default</Badge>
            <Badge variant="success">Active</Badge>
            <Badge variant="warning">Pending</Badge>
            <Badge variant="danger">Error</Badge>
            <Badge variant="info">Info</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            <Badge variant="success" dot>Online</Badge>
            <Badge variant="danger" dot>Offline</Badge>
            <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>Warning</Badge>
            <Badge variant="info" size="md" icon={<Info className="h-3.5 w-3.5" />}>Medium Info</Badge>
          </div>
          <CodeBlock>{`<Badge variant="success" dot>Active</Badge>
<Badge variant="danger" icon={<AlertTriangle />}>Critical</Badge>`}</CodeBlock>
        </Section>

        {/* ── CHIPS & STATUS DOT ── */}
        <Section id="chips" title="Chip & StatusDot" description="Compact labels and colored status indicators.">
          <div className="flex flex-wrap gap-3">
            <Chip label="React" />
            <Chip label="TypeScript" onDelete={() => notify.info('Deleted')} />
            <Chip label="Active" color="success" />
            <Chip label="Archived" color="warning" />
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <StatusDot status="online" label="Healthy" />
            <StatusDot status="warning" label="Degraded" />
            <StatusDot status="error" label="Down" />
            <StatusDot status="pending" label="Pending" />
          </div>
          <CodeBlock>{`<Chip label="React" onDelete={fn} />
<StatusDot status="online" label="Healthy" />`}</CodeBlock>
        </Section>

        {/* ── CARDS ── */}
        <Section id="cards" title="Cards" description="Themeable container with 7 design variants, header/footer/content composition, accent borders, and hover effects.">
          {/* Variant showcase */}
          <div>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Variants</Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Card variant="default">
                <CardHeader description="Standard bordered card">Default</CardHeader>
                <CardContent>Clean border with subtle shadow. The go-to for most layouts.</CardContent>
              </Card>
              <Card variant="elevated">
                <CardHeader description="Lifted with stronger shadow">Elevated</CardHeader>
                <CardContent>No visible border, relies on shadow depth.</CardContent>
              </Card>
              <Card variant="outlined">
                <CardHeader description="Border-only, completely flat">Outlined</CardHeader>
                <CardContent>Minimal appearance, ideal for dense information.</CardContent>
              </Card>
              <Card variant="ghost">
                <CardHeader description="Blends with background">Ghost</CardHeader>
                <CardContent>Subtle tinted background, no border or shadow.</CardContent>
              </Card>
              <Card variant="gradient">
                <CardHeader description="Gradient with white text">Gradient</CardHeader>
                <CardContent>Eye-catching card for CTAs and feature highlights.</CardContent>
              </Card>
              <Card variant="glass">
                <CardHeader description="Frosted glass effect">Glass</CardHeader>
                <CardContent>Backdrop blur — ideal for overlaid content.</CardContent>
              </Card>
              <Card variant="interactive">
                <CardHeader description="Accent bar on hover" actions={<Badge variant="success" dot>Live</Badge>}>Interactive</CardHeader>
                <CardContent>Lifts on hover with a primary color accent stripe.</CardContent>
              </Card>
            </div>
          </div>

          <Divider sx={{ my: 3 }} />

          {/* Accent border */}
          <div>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Accent Border</Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card accent="var(--primary-color)">
                <CardHeader>Primary Accent</CardHeader>
                <CardContent>Top border uses the platform primary color.</CardContent>
              </Card>
              <Card accent="#10b981">
                <CardHeader>Success Accent</CardHeader>
                <CardContent>Custom green accent for success states.</CardContent>
              </Card>
              <Card accent="#ef4444">
                <CardHeader>Danger Accent</CardHeader>
                <CardContent>Red accent for warnings or alerts.</CardContent>
              </Card>
            </div>
          </div>

          <Divider sx={{ my: 3 }} />

          {/* Composition */}
          <div>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Full Composition</Typography>
            <div className="max-w-sm">
              <Card variant="elevated">
                <CardHeader actions={<Badge variant="success" dot>Active</Badge>} description="PostgreSQL via SSL">Data Source</CardHeader>
                <CardContent>Production database with 12 tables, last synced 2 hours ago.</CardContent>
                <CardFooter>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Updated 2h ago</span>
                    <span className="font-mono">ID: ds_abc123</span>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>

          <CodeBlock>{`// Variant
<Card variant="elevated">...</Card>
<Card variant="glass">...</Card>
<Card variant="interactive" hoverable>...</Card>

// Accent border
<Card accent="#10b981">...</Card>

// Full composition
<Card variant="elevated">
  <CardHeader description="Subtitle" actions={<Badge>Live</Badge>}>Title</CardHeader>
  <CardContent>Body text</CardContent>
  <CardFooter>Footer</CardFooter>
</Card>`}</CodeBlock>
        </Section>

        {/* ── STAT CARDS ── */}
        <Section id="statcard" title="Stat Cards" description="KPI summary cards with trend indicators and sparklines.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Total Users" value="12,847" trend={{ value: 12.5, direction: 'up' }} />
            <StatCard title="Revenue" value="$284K" trend={{ value: 3.2, direction: 'down' }} />
            <StatCard title="Active Sessions" value="1,203" />
          </div>
          <CodeBlock>{`<StatCard title="Users" value="12,847" trend={{ value: 12.5, direction: 'up' }} />`}</CodeBlock>
        </Section>

        {/* ── ACCORDION ── */}
        <Section id="accordion" title="Accordion" description="Collapsible content sections for FAQ, settings, and detail panels.">
          <Accordion items={[
            { id: 'a1', title: 'What is AIDAS?', content: 'AIDAS is an enterprise AI analytics platform.' },
            { id: 'a2', title: 'How do permissions work?', content: 'Permissions are RBAC-based using the Can component and usePermissionGate hook.' },
            { id: 'a3', title: 'Can I customize themes?', content: 'Yes — CSS variables and MUI theming are fully supported.' },
          ]} />
          <CodeBlock>{`<Accordion items={[{ id: 'faq1', title: 'Question?', content: 'Answer.' }]} />`}</CodeBlock>
        </Section>

        {/* ── TABS ── */}
        <Section id="tabs" title="Tabs" description="4 visual variants: default (underline indicator), pills, underline (minimal), and segment (button group).">
          <div className="space-y-6">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Default</Typography>
              <Tabs value={demoTab} onChange={setDemoTab} items={[
                { id: 'tab1', label: 'Overview', icon: <Eye className="h-3.5 w-3.5" />, content: <p className="text-sm text-slate-600 dark:text-slate-400">Overview panel with summary data and KPIs.</p> },
                { id: 'tab2', label: 'Settings', icon: <SlidersHorizontal className="h-3.5 w-3.5" />, content: <p className="text-sm text-slate-600 dark:text-slate-400">Configuration and preferences panel.</p> },
                { id: 'tab3', label: 'Logs', icon: <FileText className="h-3.5 w-3.5" />, count: 24, content: <p className="text-sm text-slate-600 dark:text-slate-400">Activity log entries would appear here.</p> },
              ]} />
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Pills</Typography>
              <Tabs variant="pills" items={[
                { id: 'p1', label: 'All', count: 128, content: <p className="text-sm text-slate-600 dark:text-slate-400">All items view.</p> },
                { id: 'p2', label: 'Active', count: 94, content: <p className="text-sm text-slate-600 dark:text-slate-400">Active items only.</p> },
                { id: 'p3', label: 'Archived', count: 34, content: <p className="text-sm text-slate-600 dark:text-slate-400">Archived items.</p> },
              ]} />
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Underline (minimal)</Typography>
              <Tabs variant="underline" items={[
                { id: 'u1', label: 'General', content: <p className="text-sm text-slate-600 dark:text-slate-400">General settings panel.</p> },
                { id: 'u2', label: 'Security', content: <p className="text-sm text-slate-600 dark:text-slate-400">Security configuration.</p> },
                { id: 'u3', label: 'Notifications', content: <p className="text-sm text-slate-600 dark:text-slate-400">Notification preferences.</p> },
                { id: 'u4', label: 'Disabled', disabled: true },
              ]} />
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Segment (button group)</Typography>
              <Tabs variant="segment" size="sm" items={[
                { id: 's1', label: 'Day', content: <p className="text-sm text-slate-600 dark:text-slate-400">Daily view.</p> },
                { id: 's2', label: 'Week', content: <p className="text-sm text-slate-600 dark:text-slate-400">Weekly view.</p> },
                { id: 's3', label: 'Month', content: <p className="text-sm text-slate-600 dark:text-slate-400">Monthly view.</p> },
                { id: 's4', label: 'Year', content: <p className="text-sm text-slate-600 dark:text-slate-400">Yearly view.</p> },
              ]} />
            </div>
          </div>

          <CodeBlock>{`// Default with icons & counts
<Tabs value={tab} onChange={setTab} items={[
  { id: 'overview', label: 'Overview', icon: <Eye />, content: <div /> },
  { id: 'logs', label: 'Logs', count: 24, content: <div /> },
]} />

// Pills variant
<Tabs variant="pills" items={[...]} />

// Segment (button group)
<Tabs variant="segment" size="sm" items={[...]} />

// With extra action on the right
<Tabs items={[...]} extra={<Button size="sm">Export</Button>} />`}</CodeBlock>
        </Section>

        {/* ── DATA TABLE ── */}
        <Section id="datatable" title="Data Table" description="Full-featured table with sorting, pagination, row selection, and custom cell renderers.">
          <DataTable
            columns={[
              { key: 'name', header: 'Name', sortable: true },
              { key: 'status', header: 'Status', render: (row: any) => <Badge variant={row.status === 'Active' ? 'success' : 'warning'}>{row.status}</Badge> },
              { key: 'role', header: 'Role' },
            ]}
            data={[
              { name: 'Alice Johnson', status: 'Active', role: 'Admin' },
              { name: 'Bob Smith', status: 'Pending', role: 'Viewer' },
              { name: 'Carol White', status: 'Active', role: 'Editor' },
            ]}
          />
          <CodeBlock>{`<DataTable columns={[{ key: 'name', header: 'Name', sortable: true }]} data={rows} />`}</CodeBlock>
        </Section>

        {/* ── AVATAR ── */}
        <Section id="avatar" title="Avatar" description="User avatars with initials fallback and group stacking.">
          <div className="flex items-center gap-4">
            <Avatar name="Alice Johnson" size="lg" />
            <Avatar name="Bob Smith" />
            <Avatar name="Carol White" size="sm" />
          </div>
          <div className="mt-4">
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Avatar Group</Typography>
            <AvatarGroup max={3}>
              <Avatar name="Alice" />
              <Avatar name="Bob" />
              <Avatar name="Carol" />
              <Avatar name="Dave" />
              <Avatar name="Eve" />
            </AvatarGroup>
          </div>
          <CodeBlock>{`<Avatar name="Alice Johnson" size="lg" />
<AvatarGroup max={3}><Avatar name="A" /><Avatar name="B" /></AvatarGroup>`}</CodeBlock>
        </Section>

        {/* ── BREADCRUMBS ── */}
        <Section id="breadcrumbs" title="Breadcrumbs" description="Navigation breadcrumb trail for hierarchical pages.">
          <Breadcrumbs items={[
            { label: 'Home', href: '#' },
            { label: 'Data Sources', href: '#' },
            { label: 'PostgreSQL' },
          ]} />
          <CodeBlock>{`<Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Current' }]} />`}</CodeBlock>
        </Section>

        {/* ── PROGRESS ── */}
        <Section id="progress" title="Progress" description="Progress bars for task completion, uploads, and loading states.">
          <div className="space-y-4 max-w-md">
            <Progress value={75} label="Upload Progress" />
            <Progress value={40} color="warning" />
            <Progress value={90} color="success" label="Almost done" />
          </div>
          <CodeBlock>{`<Progress value={75} label="Uploading..." variant="success" />`}</CodeBlock>
        </Section>

        {/* ── TOOLTIP ── */}
        <Section id="tooltip" title="Tooltip" description="Hover-triggered informational popups.">
          <div className="flex gap-6">
            <Tooltip content="This is a helpful tooltip">
              <Button variant="outline">Hover me</Button>
            </Tooltip>
            <Tooltip content="Delete this item permanently" placement="right">
              <Button variant="danger" size="sm">Delete</Button>
            </Tooltip>
          </div>
          <CodeBlock>{`<Tooltip content="Help text"><Button>Hover me</Button></Tooltip>`}</CodeBlock>
        </Section>

        {/* ── SEARCH ── */}
        <Section id="search" title="Search Input" description="Consistent search with icon, clear button, and debounce-ready API.">
          <div className="flex flex-wrap gap-4 items-center">
            <SearchInput value={demoSearch} onChange={setDemoSearch} placeholder="Search components..." />
            <SearchInput value="" onChange={() => {}} placeholder="Custom width" width="20rem" />
          </div>
          {demoSearch && <p className="mt-2 text-sm text-slate-500">Searching for: <strong>{demoSearch}</strong></p>}
          <CodeBlock>{`<SearchInput value={query} onChange={setQuery} placeholder="Search..." />`}</CodeBlock>
        </Section>

        {/* ── FILE UPLOAD ── */}
        <Section id="fileupload" title="File Upload" description="Composable drag-and-drop file upload with progress tracking, retry/delete, and file type icons.">
          <div className="space-y-6">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>With Progress Fill</Typography>
              <div className="max-w-lg">
                <FileUpload.Root>
                  <FileUpload.DropZone
                    onDropFiles={(files) => notify.success(`${files.length} file(s) dropped`)}
                    accept=".csv,.xlsx,.json,.pdf"
                  />
                  <FileUpload.List>
                    <FileUpload.ListItemProgressFill
                      id="demo-1"
                      name="quarterly-report-2026.pdf"
                      type="pdf"
                      size={720 * 1024}
                      progress={100}
                      onDelete={() => notify.info('Deleted')}
                    />
                    <FileUpload.ListItemProgressFill
                      id="demo-2"
                      name="dashboard-screenshot.jpg"
                      type="jpg"
                      size={2.4 * 1024 * 1024}
                      progress={65}
                      onDelete={() => notify.info('Deleted')}
                    />
                    <FileUpload.ListItemProgressFill
                      id="demo-3"
                      name="data-export.csv"
                      type="csv"
                      size={1024 * 1024}
                      progress={0}
                      failed
                      error="Network timeout"
                      onDelete={() => notify.info('Deleted')}
                      onRetry={() => notify.info('Retrying...')}
                    />
                  </FileUpload.List>
                </FileUpload.Root>
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Simple (drop-in)</Typography>
              <div className="max-w-lg">
                <SimpleFileUpload
                  accept=".csv,.xlsx,.json"
                  maxSize={10 * 1024 * 1024}
                  label="Drop files here"
                  description="CSV, XLSX, or JSON up to 10MB"
                />
              </div>
            </div>
          </div>

          <CodeBlock>{`// Compound API
<FileUpload.Root>
  <FileUpload.DropZone onDropFiles={handleDrop} accept=".pdf,.csv" />
  <FileUpload.List>
    {files.map(f => (
      <FileUpload.ListItemProgressFill
        key={f.id} {...f}
        onDelete={() => removeFile(f.id)}
        onRetry={() => retryFile(f.id)}
      />
    ))}
  </FileUpload.List>
</FileUpload.Root>

// Simple API
<SimpleFileUpload accept=".csv" maxSize={10 * 1024 * 1024} onUpload={fn} />`}</CodeBlock>
        </Section>

        {/* ── INLINE EDIT ── */}
        <Section id="inlineedit" title="Inline Edit" description="Click-to-edit text fields for quick inline updates.">
          <div className="max-w-sm">
            <InlineEdit value={inlineVal} onSave={setInlineVal} />
          </div>
          <CodeBlock>{`<InlineEdit value={name} onSave={setName} />`}</CodeBlock>
        </Section>

        {/* ── VIEW TOGGLE ── */}
        <Section id="view-toggle" title="View Toggle" description="Grid/List switcher for collection pages.">
          <div className="flex items-center gap-4">
            <ViewToggle view={demoView} onViewChange={setDemoView} />
            <span className="text-sm text-slate-500">Current: <strong>{demoView}</strong></span>
          </div>
          <CodeBlock>{`<ViewToggle view={view} onViewChange={setView} />`}</CodeBlock>
        </Section>

        {/* ── FEEDBACK ── */}
        <Section id="feedback" title="Skeleton & Spinners" description="Loading states — use Skeleton for initial loads, Spinner for explicit waits.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Skeleton Variants</Typography>
              <div className="space-y-3">
                <Skeleton variant="text" className="w-full" />
                <Skeleton variant="text" className="w-3/4" />
                <Skeleton variant="rounded" height={60} className="w-full" />
                <div className="flex items-center gap-3">
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" lines={2} className="flex-1" />
                </div>
              </div>
            </div>
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Skeleton Card Preset</Typography>
              <SkeletonCard />
            </div>
          </div>
          <Divider sx={{ my: 3 }} />
          <div className="flex items-center gap-8">
            <Spinner size={24} label="Loading data..." />
            <Spinner size={16} />
          </div>
          <CodeBlock>{`<Skeleton variant="text" lines={3} />
<SkeletonCard />
<Spinner label="Loading data..." />`}</CodeBlock>
        </Section>

        {/* ── ALERTS ── */}
        <Section id="alerts" title="Alerts" description="Contextual alerts for success, info, warning, and error states.">
          <div className="space-y-3">
            <Alert severity="success" title="Success">Operation completed successfully.</Alert>
            <Alert severity="info">This is an informational message.</Alert>
            <Alert severity="warning" title="Warning" onClose={() => notify.info('Alert dismissed')}>This action may have side effects.</Alert>
            <Alert severity="error" title="Error">Failed to save changes. Please try again.</Alert>
          </div>
          <CodeBlock>{`<Alert severity="warning" title="Caution" onClose={handleDismiss}>
  This will delete all records.
</Alert>`}</CodeBlock>
        </Section>

        {/* ── DIALOG & DRAWER ── */}
        <Section id="dialog" title="Dialog & Drawer" description="Modal dialogs and slide-in drawer panels for forms, details, and confirmations.">
          <div className="flex gap-4">
            <Button onClick={() => setDialogOpen(true)}>Open Dialog</Button>
            <Button variant="outline" onClick={() => setDrawerOpen(true)}>Open Drawer</Button>
          </div>
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} title="Sample Dialog" subtitle="This is a reusable modal dialog.">
            <p className="text-sm text-slate-600">Dialog body content goes here.</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={() => { setDialogOpen(false); notify.success('Saved!'); }}>Save</Button>
            </DialogFooter>
          </Dialog>
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Drawer Panel">
            <p className="text-sm text-slate-600 p-4">Drawer content — great for forms and detail views.</p>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setDrawerOpen(false)}>Close</Button>
            </DrawerFooter>
          </Drawer>
          <CodeBlock>{`<Dialog open={open} onClose={close} title="Edit">
  <p>Content</p>
  <DialogFooter><Button>Save</Button></DialogFooter>
</Dialog>`}</CodeBlock>
        </Section>

        {/* ── CONFIRM DIALOG ── */}
        <Section id="confirm" title="Confirm Dialog" description="Pre-configured confirmation modals for destructive actions.">
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>Delete Item</Button>
          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => { setConfirmOpen(false); notify.success('Deleted!'); }}
            title="Delete this item?"
            description="This action cannot be undone. All associated data will be permanently removed."
            confirmText="Delete"
            variant="danger"
          />
          <CodeBlock>{`<ConfirmDialog open={open} onClose={close} onConfirm={handleDelete}
  title="Delete?" variant="danger" />`}</CodeBlock>
        </Section>

        {/* ── POPOVER ── */}
        <Section id="popover" title="Popover" description="Contextual floating panels triggered by click.">
          <Popover trigger={<Button variant="outline">Show Popover</Button>}>
            <div className="p-3 text-sm">
              <p className="font-semibold mb-1">Popover Content</p>
              <p className="text-slate-500">Any React content can go here.</p>
            </div>
          </Popover>
          <CodeBlock>{`<Popover trigger={<Button>Open</Button>}><div>Content</div></Popover>`}</CodeBlock>
        </Section>

        {/* ── TOAST ── */}
        <Section id="toast" title="Toast Notifications" description="Non-blocking success, error, info, and warning toasts via notify().">
          <div className="flex flex-wrap gap-3">
            <Button size="sm" onClick={() => notify.success('Operation succeeded!')}>Success Toast</Button>
            <Button size="sm" variant="danger" onClick={() => notify.error('Something went wrong.')}>Error Toast</Button>
            <Button size="sm" variant="outline" onClick={() => notify.info('FYI: New feature available.')}>Info Toast</Button>
            <Button size="sm" variant="ghost" onClick={() => notify.warning('Careful with this action.')}>Warning Toast</Button>
          </div>
          <CodeBlock>{`notify.success('Saved!');
notify.error('Failed to save.');
notify.info('Tip: Try keyboard shortcuts.');`}</CodeBlock>
        </Section>

        {/* ── PAGINATION ── */}
        <Section id="pagination" title="Pagination" description="Page navigation for large data sets.">
          <Pagination page={demoPage} pageSize={10} total={100} onChange={setDemoPage} />
          <p className="mt-2 text-sm text-slate-500">Current page: <strong>{demoPage}</strong></p>
          <CodeBlock>{`<Pagination page={page} pageSize={20} total={200} onChange={setPage} />`}</CodeBlock>
        </Section>

        {/* ── STEPPER ── */}
        <Section id="stepper" title="Stepper" description="Multi-step wizard progress indicator.">
          <Stepper activeStep={demoStep} steps={[
            { id: 'connect', label: 'Connect' },
            { id: 'config', label: 'Configure' },
            { id: 'review', label: 'Review' },
            { id: 'deploy', label: 'Deploy' },
          ]} />
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="ghost" disabled={demoStep <= 0} onClick={() => setDemoStep(s => s - 1)}>Back</Button>
            <Button size="sm" disabled={demoStep >= 3} onClick={() => setDemoStep(s => s + 1)}>Next</Button>
          </div>
          <CodeBlock>{`<Stepper activeStep={step} steps={[{ label: 'Step 1' }, { label: 'Step 2' }]} />`}</CodeBlock>
        </Section>

        {/* ── DROPDOWN MENU ── */}
        <Section id="dropdown" title="Dropdown Menu" description="Context menu with icons, dividers, and danger items.">
          <DropdownMenu
            trigger={<Button variant="outline" rightIcon={<ChevronDown className="h-4 w-4" />}>Actions</Button>}
            items={[
              { label: 'Edit', icon: <Eye className="h-4 w-4" />, onClick: () => notify.info('Edit clicked') },
              { label: 'Duplicate', icon: <Copy className="h-4 w-4" />, onClick: () => notify.info('Duplicated') },
              { type: 'separator' },
              { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, danger: true, onClick: () => notify.success('Deleted') },
            ]}
          />
          <CodeBlock>{`<DropdownMenu trigger={<Button>Actions</Button>} items={[
  { label: 'Edit', onClick: fn },
  { type: 'separator' },
  { label: 'Delete', danger: true, onClick: fn },
]} />`}</CodeBlock>
        </Section>

        {/* ── EMPTY STATE ── */}
        <Section id="empty" title="Empty State" description="Consistent zero-data display for all collection pages.">
          <EmptyState
            icon={<Database className="h-8 w-8 text-gray-300 dark:text-slate-500" />}
            title="No data sources found"
            description="Create your first data connection to start analyzing."
            action={<Button leftIcon={<PlusCircle className="h-4 w-4" />}>Create Connection</Button>}
          />
          <CodeBlock>{`<EmptyState
  icon={<Database className="h-8 w-8" />}
  title="No items found"
  description="Get started by creating one."
  action={<Button>Create</Button>}
/>`}</CodeBlock>
        </Section>

        {/* ── ACTION BUTTONS ── */}
        <Section id="actions" title="Action Buttons (RBAC-Gated)" description="Pre-configured action buttons that auto-hide based on user permissions.">
          <div className="flex flex-wrap gap-3">
            <ActionButton permission={PERMISSIONS.DATASOURCES_CREATE} leftIcon={<PlusCircle className="h-4 w-4" />} actionType="create">
              Create Source
            </ActionButton>
            <ActionButton permission={PERMISSIONS.DATASOURCES_DELETE} leftIcon={<Trash2 className="h-4 w-4" />} actionType="delete">
              Delete
            </ActionButton>
            <ActionButton permission={PERMISSIONS.DATASOURCES_EXPORT} leftIcon={<Download className="h-4 w-4" />} actionType="export">
              Export
            </ActionButton>
            <ActionButton permission="nonexistent:perm" leftIcon={<Eye className="h-4 w-4" />}>
              Hidden (no perm)
            </ActionButton>
          </div>
          <CodeBlock>{`<ActionButton permission={PERMISSIONS.DATASOURCES_CREATE} actionType="create"
  leftIcon={<PlusCircle />}>
  Create Source
</ActionButton>
// Auto-hides if user lacks datasources:create permission`}</CodeBlock>
        </Section>

        {/* ── COPY, REFRESH, DELETE BUTTONS ── */}
        <Section id="copyrefresh" title="Copy, Refresh & Delete Buttons" description="Pre-built action buttons with built-in feedback.">
          <div className="flex flex-wrap gap-4 items-center">
            <CopyButton value="Hello clipboard!" label="Copy Text" />
            <RefreshButton onClick={() => notify.info('Refreshed!')} />
            <DeleteButton onDelete={() => { notify.success('Item deleted!'); }} itemName="Sample Item" />
          </div>
          <CodeBlock>{`<CopyButton value="abc-123" label="Copy ID" />
<RefreshButton onClick={handleRefresh} />
<DeleteButton onDelete={handleDelete} itemName="User" />`}</CodeBlock>
        </Section>

        {/* ── PAGE HEADER ── */}
        <Section id="pageheader" title="Page Header" description="Consistent page title bar with breadcrumbs, description, and action buttons.">
          <PageHeader
            title="Data Sources"
            subtitle="Manage your connected databases and file uploads."
            actions={<Button leftIcon={<PlusCircle className="h-4 w-4" />}>New Source</Button>}
          />
          <CodeBlock>{`<PageHeader title="Users" description="Manage team members."
  actions={<Button>Invite</Button>} />`}</CodeBlock>
        </Section>

        {/* ── LAYOUT: STACK, GRID, FILTER PANEL ── */}
        <Section id="layout" title="Stack, Grid & Filter Panel" description="Layout primitives for consistent spacing and responsive grids.">
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Stack (vertical)</Typography>
          <Stack gap={2}>
            <Badge>Item 1</Badge>
            <Badge variant="info">Item 2</Badge>
            <Badge variant="success">Item 3</Badge>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Grid</Typography>
          <Grid columns={3} gap={3}>
            <Card><CardHeader>Col 1</CardHeader></Card>
            <Card><CardHeader>Col 2</CardHeader></Card>
            <Card><CardHeader>Col 3</CardHeader></Card>
          </Grid>
          <CodeBlock>{`<Stack spacing={2}><Badge>A</Badge><Badge>B</Badge></Stack>
<Grid columns={3} gap={3}><Card /><Card /><Card /></Grid>`}</CodeBlock>
        </Section>

        {/* ── USAGE RULES ── */}
        <Section id="rules" title="Usage Rules for AI & Team" description="Follow these rules to maintain consistency across the AIDAS platform.">
          <div className="space-y-1.5">
            {[
              "NEVER import from '@mui/material' directly in pages — use core/ components",
              "NEVER import from 'antd' — it is deprecated",
              "NEVER write raw <button>, <input>, <select> HTML — use core/ primitives",
              "ALWAYS use <Can permission={...}> for RBAC-gated UI sections",
              "ALWAYS use <ActionButton permission={...}> for create/edit/delete actions",
              "ALWAYS use <EmptyState> for zero-data scenarios",
              "ALWAYS use <Skeleton> instead of <Spinner> for initial data loading",
              "ALWAYS use notify from '@/components/core' for success/error notifications",
              "ALWAYS import from '@/components/core' barrel — never from individual files",
              "ALWAYS use <Badge> for status labels — never raw colored spans",
            ].map((rule, i) => (
              <div key={i} className={`flex items-start gap-2.5 px-3 py-2 rounded-md text-sm border ${
                rule.startsWith('NEVER')
                  ? 'bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30'
                  : 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30'
              }`}>
                {rule.startsWith('NEVER')
                  ? <Ban className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-500" />
                  : <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-500" />
                }
                <span className={`font-mono text-xs ${
                  rule.startsWith('NEVER')
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-emerald-700 dark:text-emerald-300'
                }`}>{rule}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* ── TIMELINE ── */}
        <Section id="timeline" title="Timeline" description="Composable step-aware timeline with status indicators, separators, and content slots. Also supports a SimpleTimeline for quick feeds.">
          <div className="space-y-8">
            {/* Composable Timeline */}
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Composable (CI/CD Pipeline)</Typography>
              <div className="max-w-lg">
                <Timeline defaultValue={3}>
                  {[
                    { id: 1, title: 'Source Code Checkout', duration: '12s', status: 'completed', desc: 'Successfully fetched latest changes from the main branch.' },
                    { id: 2, title: 'Dependency Installation', duration: '1m 45s', status: 'completed', desc: 'All npm packages installed and cached for future builds.' },
                    { id: 3, title: 'Unit & Integration Tests', duration: 'Running', status: 'active', desc: 'Running 142 test suites across the entire codebase...' },
                    { id: 4, title: 'Production Build', duration: 'Pending', status: 'pending', desc: 'Optimizing assets and generating static site pages.' },
                  ].map((step, _i, arr) => (
                    <TimelineItem key={step.id} step={step.id} isLast={step.id === arr.length}>
                      <TimelineIndicator>
                        {step.status === 'completed' ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : step.status === 'active' ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Minus className="h-3.5 w-3.5" />
                        )}
                      </TimelineIndicator>
                      <TimelineHeader>
                        <TimelineTitle>{step.title}</TimelineTitle>
                        <Badge
                          variant={step.status === 'completed' ? 'success' : step.status === 'active' ? 'info' : 'outline'}
                        >
                          {step.duration}
                        </Badge>
                      </TimelineHeader>
                      <TimelineContent>
                        {step.desc}
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              </div>
            </div>

            <Divider />

            {/* Simple Timeline */}
            <div>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Simple (Activity Feed)</Typography>
              <div className="max-w-lg">
                <SimpleTimeline
                  items={[
                    { id: '1', title: 'Deployment completed', description: 'v2.4.1 released to production', timestamp: '10:30 AM', status: 'success' },
                    { id: '2', title: 'Build started', description: 'CI pipeline triggered by push to main', timestamp: '10:28 AM', status: 'info' },
                    { id: '3', title: 'PR merged', description: '#482 — Fix date picker alignment', timestamp: '10:25 AM', status: 'default' },
                    { id: '4', title: 'Review requested', timestamp: '10:20 AM', status: 'pending' },
                  ]}
                />
              </div>
            </div>
          </div>

          <CodeBlock>{`// Composable API
<Timeline defaultValue={3}>
  <TimelineItem step={1}>
    <TimelineHeader>
      <TimelineSeparator />
      <TimelineTitle>Step title</TimelineTitle>
      <TimelineIndicator><CheckIcon /></TimelineIndicator>
    </TimelineHeader>
    <TimelineContent>Content here</TimelineContent>
  </TimelineItem>
</Timeline>

// Simple API (activity feeds)
<SimpleTimeline items={[
  { id: '1', title: 'Event', status: 'success', timestamp: '10:30' },
]} />`}</CodeBlock>
        </Section>

        {/* ── MUI DATAGRID ── */}
        <Section id="muidatagrid" title="MUI DataGrid" description="Thin wrapper around @mui/x-data-grid with the print toolbar button disabled by default. Drop-in replacement for DataGrid.">
          <div className="space-y-4">
            <Typography variant="subtitle2" fontWeight="bold">Default (print hidden, toolbar shown)</Typography>
            <MuiDataGrid
              rows={[
                { id: 1, commodity: 'Corn', trader: 'Alice Johnson', quantity: 4200, price: 5.12, total: 21504 },
                { id: 2, commodity: 'Wheat', trader: 'Bob Smith', quantity: 1800, price: 6.85, total: 12330 },
                { id: 3, commodity: 'Soybeans', trader: 'Carol White', quantity: 3100, price: 14.20, total: 44020 },
                { id: 4, commodity: 'Crude Oil', trader: 'Dave Brown', quantity: 500, price: 78.40, total: 39200 },
              ]}
              columns={[
                { field: 'commodity', headerName: 'Commodity', flex: 1 },
                { field: 'trader', headerName: 'Trader', flex: 1 },
                { field: 'quantity', headerName: 'Qty', type: 'number', width: 100 },
                { field: 'price', headerName: 'Price ($)', type: 'number', width: 120 },
                { field: 'total', headerName: 'Total ($)', type: 'number', width: 130 },
              ]}
              height={320}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold">Custom height + no toolbar</Typography>
            <MuiDataGrid
              rows={[
                { id: 1, name: 'Alpha', status: 'Active', region: 'APAC' },
                { id: 2, name: 'Beta', status: 'Inactive', region: 'EMEA' },
                { id: 3, name: 'Gamma', status: 'Active', region: 'NA' },
              ]}
              columns={[
                { field: 'name', headerName: 'Name', flex: 1 },
                { field: 'status', headerName: 'Status', width: 120 },
                { field: 'region', headerName: 'Region', width: 120 },
              ]}
              height={250}
              showToolbar={false}
            />
          </div>
          <CodeBlock>{`import { MuiDataGrid } from '@/components/core';

// Basic usage — print button hidden by default
<MuiDataGrid rows={rows} columns={columns} />

// Custom height
<MuiDataGrid rows={rows} columns={columns} height={500} />

// No toolbar at all
<MuiDataGrid rows={rows} columns={columns} showToolbar={false} />

// Re-enable print if explicitly needed
<MuiDataGrid
  rows={rows}
  columns={columns}
  printOptions={{ disableToolbarButton: false }}
/>`}</CodeBlock>
        </Section>

        {/* ── MUI DATETIMEPICKER ── */}
        <Section id="muidatetimepicker" title="MUI DateTimePicker" description="Wrapper around @mui/x-date-pickers/DateTimePicker that automatically configures LocalizationProvider and AdapterDayjs.">
          <div className="space-y-4">
            <Typography variant="subtitle2" fontWeight="bold">Basic DateTimePicker (with DemoContainer)</Typography>
            <div className="max-w-md">
              <DemoContainer components={['DateTimePicker']}>
                <MuiDateTimePicker
                  label="Basic date time picker"
                  value={demoMuiDateTime}
                  onChange={(newValue) => setDemoMuiDateTime(newValue)}
                />
              </DemoContainer>
            </div>
          </div>
          <CodeBlock>{`import { MuiDateTimePicker } from '@/components/core';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';

const [value, setValue] = useState<Dayjs | null>(null);

<DemoContainer components={['DateTimePicker']}>
  <MuiDateTimePicker
    label="Basic date time picker"
    value={value}
    onChange={(newValue) => setValue(newValue)}
  />
</DemoContainer>`}</CodeBlock>
        </Section>

        {/* Bottom spacer */}
        <div className="h-20" />
      </Box>
    </Box>
  );
}
